import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Map, CheckCircle2, FileText, Flag, Download, HeartHandshake } from "lucide-react";

const LANE_LABELS = {
  sell_home: "Selling the Home", keep_home: "Keep Mama Home", placement: "Senior Living Placement",
  downsize: "Downsizing & Estate Support", need_roadmap: "Family Transition Roadmap",
};
const laneLabel = (l) => LANE_LABELS[l] || (l ? l.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

const Section = ({ icon: Icon, title, children, testid }) => (
  <Card className="p-5 border-[#E6EDF2]" data-testid={testid}>
    <div className="flex items-center gap-1.5 font-display font-semibold text-[#1C4358] mb-3"><Icon size={18} /> {title}</div>
    {children}
  </Card>
);

export default function RoadmapPortal() {
  const [r, setR] = useState(undefined);

  useEffect(() => { api.get("/portal/roadmap").then(({ data }) => setR(data.roadmap)).catch(() => setR(null)); }, []);

  if (r === undefined) return <div className="space-y-4"><Skeleton className="h-28" /><Skeleton className="h-40" /></div>;

  if (!r) {
    return (
      <Card className="p-10 text-center border-[#E6EDF2]" data-testid="roadmap-empty">
        <HeartHandshake size={40} className="mx-auto text-[#1C4358]/40 mb-3" />
        <div className="font-display font-semibold text-[18px] text-[#252221]">Your roadmap is on the way</div>
        <p className="text-[15px] text-muted-foreground mt-1 max-w-md mx-auto">Your MovingMyMama guide is preparing your Family Transition Roadmap. It will appear here as soon as it's ready.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="roadmap-portal-page">
      <Card className="p-6 border-0 text-white" style={{ background: "linear-gradient(135deg,#1C4358 0%,#0f2733 100%)" }}>
        <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-white/15 rounded-full px-3 py-1"><Map size={13} /> Family Transition Roadmap</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold mt-3">{r.title}</h1>
        {r.situation_summary && <p className="text-[15px] text-white/85 mt-2 max-w-2xl leading-relaxed">{r.situation_summary}</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          {(r.service_lanes || []).filter(Boolean).map((l) => <span key={l} className="text-[13px] bg-white/15 rounded-full px-3 py-1">{laneLabel(l)}</span>)}
          {r.urgency && <span className="text-[13px] bg-[#9C1E22] rounded-full px-3 py-1 capitalize">{r.urgency} urgency</span>}
        </div>
      </Card>

      {r.main_concern && (
        <Section icon={Flag} title="What matters most right now" testid="roadmap-concern">
          <p className="text-[15px] text-[#252221] leading-relaxed">{r.main_concern}</p>
        </Section>
      )}

      {(r.next_steps || []).length > 0 && (
        <Section icon={CheckCircle2} title="Recommended next steps" testid="roadmap-next-steps">
          <div className="space-y-3">
            {r.next_steps.map((s, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-[#2F7D5A]/12 text-[#2F7D5A] text-[13px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div><div className="text-[15px] font-medium text-[#252221]">{s.title}</div>{s.description && <div className="text-[13px] text-muted-foreground">{s.description}</div>}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(r.documents_needed || []).length > 0 && (
        <Section icon={FileText} title="Documents that will help" testid="roadmap-documents">
          <ul className="space-y-1.5">
            {r.documents_needed.map((d, i) => <li key={i} className="text-[15px] text-[#252221] flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#1C4358]" /> {d}</li>)}
          </ul>
        </Section>
      )}

      {(r.milestones || []).length > 0 && (
        <Section icon={Flag} title="Milestones" testid="roadmap-milestones">
          <div className="space-y-2">
            {r.milestones.map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-[15px]"><CheckCircle2 size={18} className={m.status === "done" ? "text-[#2F7D5A]" : "text-[#D6E0E7]"} /><span className={m.status === "done" ? "line-through text-[#9AA5B1]" : "text-[#252221]"}>{m.title}</span></div>
            ))}
          </div>
        </Section>
      )}

      <div className="flex justify-end">
        <Button onClick={() => window.print()} variant="outline" className="border-[#D6E0E7] text-[#1C4358] gap-1.5" data-testid="roadmap-download-btn"><Download size={15} /> Print my roadmap</Button>
      </div>
    </div>
  );
}
